import { makeAutoObservable, runInAction } from 'mobx';
import axiosInstance from '../../../services/axiosInstance';
import type {
  Order,
  OrderListResponse,
  OrderDetailResponse,
  OrderStatus,
  PaymentStatus,
  OrderComment,
  CreateCommentRequest,
  UpdateCommentRequest,
} from '../types/order';

export class OrderStore {
  orders: Order[] = [];
  selectedOrder: Order | null = null;
  loading = false;
  refreshing = false;
  error: string | null = null;

  // Pagination
  currentPage = 1;
  pageSize = 50;
  totalOrders = 0;
  totalPages = 0;

  constructor() {
    makeAutoObservable(this);
  }

  // Get plugin-specific endpoint
  private getPluginEndpoint(path: string): string {
    const pluginId = localStorage.getItem('web_plugin_id');
    if (!pluginId) {
      throw new Error('Plugin ID not initialized');
    }
    return `/studio/cms/plugins/${pluginId}${path}`;
  }

  // Get computed order lists
  get newOrders() {
    return {
      pending: this.orders.filter(o => o.status.toLowerCase() === 'pending'),
      inprogress: this.orders.filter(o =>
        ['confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(o.status.toLowerCase())
      ),
    };
  }

  // Fetch all orders with pagination
  async fetchOrders(page = 1, limit = 50) {
    this.loading = true;
    this.error = null;

    try {
      const endpoint = this.getPluginEndpoint('/list');
      const response = await axiosInstance.get<OrderListResponse>(endpoint, {
        params: {
          model: 'orders',
          page,
          limit,
          sort: 'created_at',
          order: 'desc',
        },
      });

      runInAction(() => {
        this.orders = response.data.data;
        this.currentPage = response.data.meta.pagination.page;
        this.pageSize = response.data.meta.pagination.limit;
        this.totalOrders = response.data.meta.pagination.total;
        this.totalPages = response.data.meta.pagination.pages;
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch orders';
        this.loading = false;
      });
      console.error('Error fetching orders:', error);
    }
  }

  // Refresh orders (pull-to-refresh)
  async refreshOrders() {
    this.refreshing = true;
    this.error = null;

    try {
      const endpoint = this.getPluginEndpoint('/list');
      const response = await axiosInstance.get<OrderListResponse>(endpoint, {
        params: {
          model: 'orders',
          page: 1,
          limit: this.pageSize,
          sort: 'created_at',
          order: 'desc',
        },
      });

      runInAction(() => {
        this.orders = response.data.data;
        this.currentPage = 1;
        this.totalOrders = response.data.meta.pagination.total;
        this.totalPages = response.data.meta.pagination.pages;
        this.refreshing = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to refresh orders';
        this.refreshing = false;
      });
      console.error('Error refreshing orders:', error);
    }
  }

  // Fetch single order by ID
  async fetchOrderById(orderId: string) {
    this.loading = true;
    this.error = null;

    try {
      const endpoint = this.getPluginEndpoint(`/data/orders/${orderId}`);
      const response = await axiosInstance.get<OrderDetailResponse>(endpoint);

      runInAction(() => {
        this.selectedOrder = response.data.data;
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch order details';
        this.loading = false;
      });
      console.error('Error fetching order:', error);
    }
  }

  // Update order status
  async updateOrderStatus(orderId: string, status: string) {
    try {
      const endpoint = this.getPluginEndpoint(`/data/orders/${orderId}`);
      const response = await axiosInstance.put<OrderDetailResponse>(endpoint, { status });

      runInAction(() => {
        const updatedOrder = response.data.data;

        // Update in orders list
        const index = this.orders.findIndex((o) => o.id === orderId);
        if (index !== -1) {
          this.orders[index] = updatedOrder;
        }

        // Update selected order if it matches
        if (this.selectedOrder?.id === orderId) {
          this.selectedOrder = updatedOrder;
        }
      });

      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update order status';
      });
      console.error('Error updating order status:', error);
      return false;
    }
  }

  // Update payment status
  async updatePaymentStatus(orderId: string, paymentStatus: PaymentStatus) {
    try {
      const endpoint = this.getPluginEndpoint(`/data/orders/${orderId}`);
      const response = await axiosInstance.put<OrderDetailResponse>(endpoint, { payment_status: paymentStatus });

      runInAction(() => {
        const updatedOrder = response.data.data;

        // Update in orders list
        const index = this.orders.findIndex((o) => o.id === orderId);
        if (index !== -1) {
          this.orders[index] = updatedOrder;
        }

        // Update selected order if it matches
        if (this.selectedOrder?.id === orderId) {
          this.selectedOrder = updatedOrder;
        }
      });

      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update payment status';
      });
      console.error('Error updating payment status:', error);
      return false;
    }
  }

  // Clear selected order
  clearSelectedOrder() {
    this.selectedOrder = null;
  }

  // Clear error
  clearError() {
    this.error = null;
  }
}

export const orderStore = new OrderStore();
